import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { resendVerification } from '../../services/api';

const Container = styled.div`
  max-width: 600px;
  margin: 2rem auto;
  padding: 2rem;
  text-align: center;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: #4a90e2;
  margin-bottom: 1.5rem;
`;

const Message = styled.p`
  font-size: 1.1rem;
  color: #333;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background-color: #4a90e2;
  color: white;
  text-decoration: none;
  border-radius: 4px;
  font-weight: 500;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #357abd;
  }
`;

const RegistrationSuccess = () => {
  const location = useLocation();
  const registeredEmail = location.state?.email;
  const [resendStatus, setResendStatus] = useState(null); // 'success' | 'error' | null
  const [resendMessage, setResendMessage] = useState('');
  const [cooldown, setCooldown] = useState(30); // seconds until resend is allowed

  useEffect(() => {
    if (!registeredEmail) return;
    if (cooldown <= 0) return;

    const timer = setTimeout(() => {
      setCooldown((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);

    return () => clearTimeout(timer);
  }, [cooldown, registeredEmail]);

  const handleResend = async () => {
    if (!registeredEmail) return;
    if (cooldown > 0) return;
    try {
      setResendStatus(null);
      setResendMessage('');
      const response = await resendVerification(registeredEmail);
      setResendStatus('success');
      setResendMessage(response.data?.message || 'Verification email resent.');
      setCooldown(30); // reset cooldown after a successful resend
    } catch (error) {
      setResendStatus('error');
      const message = error.response?.data?.message || 'Could not resend verification email. Please try again later.';
      setResendMessage(message);
    }
  };

  return (
    <Container>
      <Title>🎉 Registration Successful!</Title>
      <Message>
        Thank you for registering! Your account has been created successfully.
        {registeredEmail && (
          <> We've sent a verification email to <strong>{registeredEmail}</strong>. </>
        )}
        You can now log in to your account and start managing your appointments. Please check your
        Junk or Spam folder if you do not see it.
      </Message>
      {registeredEmail && (
        <p>
          Didn't get the email?{' '}
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0}
          >
            {cooldown > 0
              ? `Resend available in ${cooldown}s`
              : 'Resend verification email'}
          </button>
        </p>
      )}
      {resendStatus && (
        <p style={{ color: resendStatus === 'success' ? 'green' : 'red' }}>{resendMessage}</p>
      )}
      <Button to={{ pathname: '/login' }} state={{ fromRegistration: true }}>Go to Login</Button>
    </Container>
  );
};

export default RegistrationSuccess;
